import type { QueryKey } from "@tanstack/react-query";
import type {
  RealtimeActor,
  RealtimeEnvelope,
  RealtimeFactRef,
} from "@/types/realtime.type";
import { brandUniverseChatProposalsKey } from "./useBrandUniverseChatProposals";
import { brandUniversePendingFactProposalsKey } from "./useBrandUniversePendingFactProposals";
import { brandUniverseGraphKey, removeFactsFromGraph } from "./universeCache";
import type { RealtimeHandler } from "./realtimeHandlers";

/** Brand Universe: which cached reads each realtime event touches. */
export const UNIVERSE_REALTIME: RealtimeHandler = {
  apply(qc, event) {
    const { brandId } = event;
    const invalidate = (queryKey: QueryKey) => qc.invalidateQueries({ queryKey });

    switch (event.type) {
      case "universe.facts.removed":
        // Straight out of the cache, so the planet goes now, not after a refetch.
        removeFactsFromGraph(qc, brandId, event.data.facts.map((f) => f.id));
        void invalidate(brandUniverseGraphKey(brandId));
        return;
      case "universe.facts.added":
        // The graduated signal itself arrives as its own `signals.changed`.
        void invalidate(brandUniverseGraphKey(brandId));
        return;
      case "universe.facts.updated":
      case "universe.entities.changed":
        void invalidate(brandUniverseGraphKey(brandId));
        return;
      case "universe.graph.rebuilt":
        void invalidate(brandUniverseGraphKey(brandId));
        void invalidate(brandUniversePendingFactProposalsKey(brandId));
        return;
      case "universe.proposals.changed": {
        const { action, sessionId, acceptedFacts } = event.data;
        const refreshProposals = () =>
          Promise.all([
            invalidate(brandUniversePendingFactProposalsKey(brandId)),
            invalidate(brandUniverseChatProposalsKey(brandId, sessionId)),
          ]);
        if (action === "decided" && acceptedFacts?.length) {
          // Graph first: if the tray emptied before the new facts landed, the
          // universe would briefly look like it lost them.
          void invalidate(brandUniverseGraphKey(brandId)).then(refreshProposals);
        } else {
          void refreshProposals();
        }
        return;
      }
    }
  },

  resyncKeys: (brandId) => [
    brandUniverseGraphKey(brandId),
    brandUniversePendingFactProposalsKey(brandId),
    ["brandUniverseChatProposals", brandId],
  ],
};

/**
 * Who a toast names: nobody for the viewer's own changes (other tabs included)
 * or for system work, which nobody on the team did.
 */
function otherActorName(actor: RealtimeActor, viewerId: string | null): string | null {
  switch (actor.kind) {
    case "user":
      return actor.userId === viewerId ? null : (actor.name ?? "A teammate");
    case "agent":
      if (!actor.onBehalfOf || actor.onBehalfOf.userId === viewerId) return null;
      return `The agent (for ${actor.onBehalfOf.name ?? "a teammate"})`;
    case "system":
      return null;
  }
}

function factCount(facts: RealtimeFactRef[]) {
  return facts.length === 1 ? "a fact" : `${facts.length} facts`;
}

function factNames(facts: RealtimeFactRef[]) {
  const shown = facts.slice(0, 3).map((f) => `“${f.name}”`);
  const more = facts.length - shown.length;
  return more > 0 ? `${shown.join(", ")} and ${more} more` : shown.join(", ");
}

/** The toast a teammate's change earns on the Universe page, if any. */
export function universeActivityToast(
  event: RealtimeEnvelope,
  viewerId: string | null,
): { title: string; description?: string } | null {
  const who = otherActorName(event.actor, viewerId);
  if (!who) return null;

  switch (event.type) {
    case "universe.facts.removed":
      return {
        title: `${who} deleted ${factCount(event.data.facts)}`,
        description: factNames(event.data.facts),
      };
    case "universe.facts.added":
      return {
        title: `${who} added ${factCount(event.data.facts)} from a signal`,
        description: factNames(event.data.facts),
      };
    case "universe.proposals.changed": {
      const accepted = event.data.acceptedFacts ?? [];
      if (event.data.action !== "decided" || accepted.length === 0) return null;
      return {
        title: `${who} added ${factCount(accepted)}`,
        description: factNames(accepted),
      };
    }
    case "universe.entities.changed": {
      const name = `“${event.data.name}”`;
      const title = {
        created: `${who} added competitor ${name}`,
        updated: `${who} updated competitor ${name}`,
        deleted: `${who} removed competitor ${name}`,
        merged: `${who} merged a competitor into ${name}`,
      }[event.data.action];
      return { title };
    }
    default:
      return null;
  }
}
